import { useState } from 'react';
import { Picker } from './ProjectsView';
import { projectLabel, projectColorOf, flattenTree, NO_PROJECT, NO_PROJECT_COLOR } from './projects';
import './NewRail.css';

// ============================================================
// NEW RAIL — the light surface inside the dark board.
//
// One row per untriaged arrival, readable in two seconds from six feet:
// a lane bar, one line of meaning, one line of who/when, up to three
// chips, an italic ask. Nothing else. The same row draws the sweep's
// PROPOSED rail (mode="proposal") from the dry-run report, so the two
// surfaces are the same surface.
//
// Phase C: a PROJECT chip after the lane bar, before the title — the
// project's path in its color, dashed while the placement is the sweep's
// guess (projectLocked false / absent), solid once Marc placed it; an empty
// "+ project" ghost when the card has none. Tapping it (triage rows only —
// a proposal has no card yet) opens the Projects view's Move to… picker and
// hands the pick to onProject(row, projectId), which the parent writes at
// once — the card exists, it just is not triaged.
//
// Pure presentation. No MSAL, no fetch — every write goes back through
// onAction / onAttach / onKillAll / onProject. Importable under jest as-is.
// ============================================================

export const CONFIDENCE_MIN = 0.5;        // below this: subject only, no chips but court, no ask
export const DEADLINE_SOON_DAYS = 7;
export const LANES = ['FIG', 'Family', 'Body', 'Platform', 'Noise'];

// The lane palette. Reused on TODAY later — keep it here, not in Watch.js.
export const LANE_COLORS = {
  FIG: '#1f4e9e',        // deep blue
  Family: '#4f9a5b',     // warm green
  Body: '#e8735c',       // coral
  Platform: '#64748b',   // slate
  Noise: '#c8ccd2',      // light gray
};
const LANE_NONE = '#d9dde3';

const DAY_MS = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── helpers (exported for tests) ──

// "Re: Fwd: [EXTERNAL] [Event Sep 9] Gulf Steel" → "Gulf Steel"
// A separator a stripped tag leaves behind goes too:
//   "Fwd: [Event Deadline Approaching] - MSS Meet #1" → "MSS Meet #1"
//   "Fwd: - MTS Fall Invite"                          → "MTS Fall Invite"
//   "RE: FW: - Something"                             → "Something"
//   "Re: Wortham - Cancellation"                      → "Wortham - Cancellation"
//   "Subject: Green Span 9/29 renewal"                → "Green Span 9/29 renewal"
export function cleanSubject(s) {
  let t = String(s || '').trim();
  const prefix = /^(?:(?:re|fw|fwd|aw|wg|subject)\s*:\s*|\[[^\]]*\]\s*|[-–—]\s*)+/i;
  let prev;
  do { prev = t; t = t.replace(prefix, '').trim(); } while (t !== prev && t);
  return t || String(s || '').trim();
}

function parseLocal(s) {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m && s.length === 10) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// "today" · "1d ago" · "3d ago" · "Aug 14" past a month.
export function relativeTime(when, now = new Date()) {
  const d = parseLocal(when);
  if (!d) return '';
  const days = Math.round((startOfDay(now) - startOfDay(d)) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  if (days <= 30) return `${days}d ago`;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

export function fmtShortDate(s) {
  const d = parseLocal(s);
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}` : '';
}

// 'past' (gray) · 'soon' (red, inside 7 days) · 'later' (neutral)
export function deadlineTone(deadline, today = new Date()) {
  const d = parseLocal(deadline);
  if (!d) return null;
  const days = Math.round((startOfDay(d) - startOfDay(today)) / DAY_MS);
  if (days < 0) return 'past';
  if (days <= DEADLINE_SOON_DAYS) return 'soon';
  return 'later';
}

export const isLowConfidence = (row) => !(typeof row.confidence === 'number' && row.confidence >= CONFIDENCE_MIN);
export const isNoise = (row) => !isLowConfidence(row) && row.lane === 'Noise';

// A display name, never a raw address when a name exists.
function displayWho(who) {
  const w = String(who || '').trim();
  if (!w) return '';
  if (!w.includes('@')) return w;
  const local = w.split('@')[0];
  return local.replace(/[._-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
const orgOf = (org, who) => {
  const o = String(org || '').trim();
  if (o) return o.replace(/\.(com|net|org|invalid)$/i, '');
  const w = String(who || '');
  return w.includes('@') ? w.split('@')[1].split('.')[0] : '';
};

// ── row builders ──

// The chip's data: { id, label, color, locked } or null when the card has
// no project. `tree` is the projects.js tree (label + color come from it).
export function projectChip(projectId, locked, tree, fallbackLabel = '') {
  if (!projectId) return null;
  const label = tree?.byId?.has(projectId) ? projectLabel(tree, projectId) : (fallbackLabel || projectId);
  return { id: projectId, label, color: projectColorOf(tree, projectId), locked: locked === true };
}

// A NEW item on the board (Watch_Open row + sources + intake) → row.
export function rowFromItem(item, byId = new Map(), tree = null) {
  const srcs = [...(item.sources || [])].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const latest = srcs[srcs.length - 1] || {};
  const intake = item.intake || null;
  const suggest = intake?.attachSuggested || null;
  return {
    key: `n:${item.id}`,
    id: item.id,
    subject: cleanSubject(item.title),
    title: '',                                   // the card's Title is already the sweep's human title (or the operator's)
    project: projectChip(item.projectId, item.projectLocked, tree),
    summary: intake?.summary || '',
    ask: intake?.ask || '',
    deadline: intake?.deadline || '',
    lane: intake?.lane || '',
    confidence: intake?.confidence ?? null,
    who: displayWho(latest.who || item.counterparty),
    org: orgOf(latest.org, latest.who || item.counterparty),
    when: latest.date || item.lastTouched || '',
    court: item.court || '',
    attachSuggested: suggest,
    attachTitle: suggest ? (byId.get(suggest)?.title || '') : '',
  };
}

// A PROPOSED row from the sweep's dry-run report → row (same shape). The
// report carries the sweep's title and project guess (always unlocked).
export function rowFromProposal(r, byId = new Map(), tree = null) {
  const ex = r.extraction || null;
  const p = r.proving || {};
  const suggest = r.attachSuggested || null;
  return {
    key: `p:${r.key}`,
    id: null,
    proposal: r,
    subject: cleanSubject(r.subject),
    title: r.title || '',
    project: projectChip(r.projectId, false, tree, r.projectName),
    summary: ex?.summary || '',
    ask: ex?.ask || '',
    deadline: ex?.deadline || '',
    lane: ex?.lane || '',
    confidence: ex?.confidence ?? null,
    who: displayWho(p.fromName || p.from || r.counterparty),
    org: orgOf('', p.from || r.counterparty),
    when: p.ct || '',
    court: r.court || '',
    // the model's attachTo first; the sweep's would-be sender card as the fallback (d)
    attachSuggested: suggest || r.suggestedWatchId || null,
    attachBy: suggest ? 'model' : (r.suggestedWatchId ? 'sender' : ''),
    attachTitle: (suggest || r.suggestedWatchId) ? (byId.get(suggest || r.suggestedWatchId)?.title || '') : '',
  };
}

// ── icons (inline, currentColor) ──
const Icon = {
  check: <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  pause: <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M7 4v12M13 4v12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>,
  x:     <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>,
  link:  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M8.5 11.5l3-3M7 13a3 3 0 010-4.2l1.8-1.8M13 7a3 3 0 010 4.2l-1.8 1.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>,
};

// Per mode: which icon buttons a row carries and which key fires each.
const ACTIONS = {
  triage: [
    { action: 'accept', key: 'a', icon: Icon.check, label: 'Accept (A)', cls: 'NewRail-btn--accept' },
    { action: 'park',   key: 'p', icon: Icon.pause, label: 'Park for a week (P)', cls: 'NewRail-btn--park' },
    { action: 'kill',   key: 'k', icon: Icon.x,     label: 'Kill (K)', cls: 'NewRail-btn--kill' },
  ],
  proposal: [
    { action: 'attachPick', key: 't', icon: Icon.link, label: 'Attach to a card… (T)', cls: 'NewRail-btn--attach' },
    { action: 'dismiss',    key: 'k', icon: Icon.x,    label: 'Dismiss (K)', cls: 'NewRail-btn--kill' },
  ],
};

// ── accept strip (D1.5) ──
// Check / A on a triage row opens this instead of accepting silently:
// Court (Mine / Theirs, prefilled from the sweep's guess), a one-line note,
// and a wake / due date (prefilled from the extraction deadline). Enter or
// Accept commits { court, note, date }; Esc cancels.
export function AcceptStrip({ row, onCommit, onCancel }) {
  const [court, setCourt] = useState(row.court === 'Theirs' ? 'Theirs' : 'Mine');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(row.deadline || '');
  const commit = () => onCommit({ court, note: note.trim(), date });
  const keys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); commit(); }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
  };
  return (
    <div className="NewRail-accept" data-testid="accept-strip" onKeyDown={keys}>
      <div className="NewRail-accept-court" role="group" aria-label="Court">
        {['Mine', 'Theirs'].map(c => (
          <button key={c} type="button" className={'NewRail-seg' + (court === c ? ' NewRail-seg--on' : '')} aria-pressed={court === c} onClick={() => setCourt(c)}>{c}</button>
        ))}
      </div>
      <input className="NewRail-accept-note" autoFocus placeholder="Note (optional)" aria-label="Note" value={note} onChange={e => setNote(e.target.value)} />
      <input className="NewRail-accept-date" type="date" aria-label={court === 'Mine' ? 'Due date' : 'Wake date'} value={date} onChange={e => setDate(e.target.value)} />
      <button type="button" className="NewRail-accept-go" onClick={commit}>Accept</button>
      <button type="button" className="NewRail-accept-cancel" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// The project chip / ghost and its picker. `canPick` = a triage row with a
// card behind it; a proposal shows the chip read-only.
function ProjectChip({ row, canPick, tree, onProject }) {
  const [picking, setPicking] = useState(false);
  const chip = row.project;
  const options = [
    ...flattenTree(tree || { roots: [] }).map(n => ({ id: n.id, label: n.name, color: n.color, depth: n.depth - 1, hint: n.depth > 1 ? n.path.slice(0, -1).join(' › ') : '' })),
    { id: NO_PROJECT, label: 'No project', color: NO_PROJECT_COLOR, depth: 0 },
  ];
  if (!chip && !canPick) return null;
  const open = (e) => { e.stopPropagation(); if (canPick) setPicking(p => !p); };
  return (
    <span className="NewRail-project" onClick={e => e.stopPropagation()}>
      {chip ? (
        <button type="button" className={'NewRail-chip NewRail-chip--project' + (chip.locked ? ' NewRail-chip--placed' : ' NewRail-chip--guess') + (canPick ? '' : ' NewRail-chip--static')}
          style={{ '--proj': chip.color }} title={chip.locked ? `${chip.label} — placed by you` : `${chip.label} — the sweep's guess; tap to change`}
          aria-label={`Project ${chip.label}${chip.locked ? '' : ' (guess)'}`} disabled={!canPick} onClick={open}>
          {chip.label}
        </button>
      ) : (
        <button type="button" className="NewRail-chip NewRail-chip--project NewRail-chip--ghost" aria-label="Set project" title="No project yet — tap to place" onClick={open}>+ project</button>
      )}
      {picking && (
        <Picker testId={`rail-project-picker-${row.id}`} placeholder="Move to…" options={options} onCancel={() => setPicking(false)}
          onPick={(pid) => { setPicking(false); onProject?.(row, pid); }} />
      )}
    </span>
  );
}

// ── row ──
export function NewRow({ row, today, mode = 'triage', busy = false, collapsed = false, onAction, onAttach, onProject, tree = null, children }) {
  const [accepting, setAccepting] = useState(false);
  const low = isLowConfidence(row);
  // Phase C: the sweep's human title first; the extraction summary, then the cleaned subject, as before.
  const title = row.title || ((!low && row.summary) ? row.summary : row.subject);
  const laneColor = (!low && LANE_COLORS[row.lane]) || LANE_NONE;
  const tone = !low && row.deadline ? deadlineTone(row.deadline, today) : null;
  const acts = ACTIONS[mode] || ACTIONS.triage;
  const attachLabel = row.attachSuggested ? `Attach to card #${row.attachSuggested}${row.attachTitle ? ` — ${row.attachTitle}` : ''}` : '';

  // accept in triage mode opens the strip; every other action fires at once
  const fire = (action) => {
    if (mode === 'triage' && action === 'accept') { setAccepting(a => !a); return; }
    onAction?.(row, action);
  };
  const onKeyDown = (e) => {
    if (busy || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.target.closest && e.target.closest('.NewRail-accept')) return;   // typing in the strip
    if (e.key === 'Escape' && accepting) { e.preventDefault(); setAccepting(false); return; }
    const a = acts.find(x => x.key === e.key.toLowerCase());
    if (a) { e.preventDefault(); fire(a.action); }
  };
  const confirmAttach = () => {
    if (!row.attachSuggested) return;
    if (window.confirm(`${attachLabel}?`)) onAttach?.(row, row.attachSuggested);
  };

  return (
    <div className={'NewRail-row' + (collapsed ? ' NewRail-row--collapsed' : '') + (busy ? ' NewRail-row--busy' : '')}
      style={{ '--lane': laneColor }} tabIndex={0} onKeyDown={onKeyDown} data-row-key={row.key} data-testid={row.key} aria-busy={busy || undefined}>
      <span className="NewRail-lane" aria-hidden="true" />
      <div className="NewRail-body">
        <ProjectChip row={row} canPick={mode === 'triage' && row.id != null && typeof onProject === 'function'} tree={tree} onProject={onProject} />
        <div className="NewRail-title" title={row.subject !== title ? row.subject : undefined}>{title}</div>
        {!collapsed && (
          <div className="NewRail-meta">
            {row.who && <span className="NewRail-who">{row.who}</span>}
            {row.org && <span className="NewRail-org">{row.org}</span>}
            {row.when && <span className="NewRail-when">{relativeTime(row.when, today)}</span>}
          </div>
        )}
        {!collapsed && !low && row.ask && <div className="NewRail-ask">{row.ask}</div>}
      </div>
      <div className="NewRail-chips">
        {!low && row.deadline && tone && (
          <span className={`NewRail-chip NewRail-chip--deadline NewRail-chip--${tone}`} title={tone === 'past' ? 'Deadline passed' : 'Deadline'}>{fmtShortDate(row.deadline)}</span>
        )}
        {row.court && <span className={`NewRail-pill NewRail-pill--${row.court.toLowerCase()}`}>{row.court}</span>}
        {row.attachSuggested && (!low || row.attachBy === 'sender') && (
          <button type="button" className="NewRail-chip NewRail-chip--attach" title={attachLabel} disabled={busy} onClick={confirmAttach}>
            → card #{row.attachSuggested}{row.attachBy === 'sender' && <span className="NewRail-chip-hint">by sender</span>}
          </button>
        )}
      </div>
      <div className="NewRail-actions NewRail-actions--persistent" data-testid="row-actions">
        {acts.map(a => (
          <button key={a.action} type="button" className={`NewRail-btn ${a.cls}`} title={a.label} aria-label={a.label} disabled={busy}
            onClick={() => fire(a.action)}>{a.icon}</button>
        ))}
      </div>
      {accepting && (
        <div className="NewRail-extra">
          <AcceptStrip row={row} onCancel={() => setAccepting(false)}
            onCommit={(payload) => { setAccepting(false); onAction?.(row, 'accept', payload); }} />
        </div>
      )}
      {children && <div className="NewRail-extra">{children}</div>}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="NewRail-list" aria-busy="true" aria-label="Loading">
      {[0, 1, 2].map(i => (
        <div key={i} className="NewRail-row NewRail-row--skeleton" data-testid="NewRail-skeleton">
          <span className="NewRail-lane" />
          <div className="NewRail-body">
            <div className="NewRail-sk NewRail-sk--title" />
            <div className="NewRail-sk NewRail-sk--meta" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── rail ──
// rows       built by rowFromItem / rowFromProposal
// mode       'triage' (A/P/K) or 'proposal' (attach / dismiss)
// busyKeys   Set of row.key with a write in flight
// onAction   (row, action)   onAttach (row, watchId)   onKillAll (noiseRows)
// renderExtra(row) → node under the row (the proposal attach picker)
export default function NewRail({
  rows = [], today = new Date(), mode = 'triage', loading = false, busyKeys = new Set(),
  onAction, onAttach, onKillAll, onProject, tree = null, renderExtra, error = null, onClearError,
  emptyText = 'Nothing new since the last sweep.',
}) {
  const [noiseOpen, setNoiseOpen] = useState(false);
  const live = rows.filter(r => !isNoise(r));
  const noise = rows.filter(isNoise);
  const killAllLabel = mode === 'proposal' ? 'Dismiss all' : 'Kill all';

  return (
    <div className="NewRail" data-mode={mode}>
      {error && (
        <div className="NewRail-error" role="alert">
          <span>{error}</span>
          {onClearError && <button type="button" className="NewRail-error-close" aria-label="Dismiss error" onClick={onClearError}>{Icon.x}</button>}
        </div>
      )}
      {loading ? <Skeleton /> : rows.length === 0 ? (
        <div className="NewRail-empty">{emptyText}</div>
      ) : (
        <div className="NewRail-list">
          {live.map(row => (
            <NewRow key={row.key} row={row} today={today} mode={mode} busy={busyKeys.has(row.key)} onAction={onAction} onAttach={onAttach} onProject={onProject} tree={tree}>
              {renderExtra ? renderExtra(row) : null}
            </NewRow>
          ))}
          {noise.length > 0 && (
            <div className="NewRail-noise">
              <div className="NewRail-noise-header">
                <button type="button" className="NewRail-noise-toggle" aria-expanded={noiseOpen} onClick={() => setNoiseOpen(o => !o)}>
                  Probably noise ({noise.length})
                </button>
                <button type="button" className="NewRail-noise-killall" onClick={() => onKillAll?.(noise)}>{killAllLabel}</button>
              </div>
              {noiseOpen && noise.map(row => (
                <NewRow key={row.key} row={row} today={today} mode={mode} collapsed busy={busyKeys.has(row.key)} onAction={onAction} onAttach={onAttach} onProject={onProject} tree={tree} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
